import React, { ReactElement } from 'react'
import { Stack, Text, Link, VStack, HStack, Box } from '@chakra-ui/react'
import { AlertCenterContainer, MetricSummary } from '@apex/component-library'
import styles from './styles.module.less'
import { numberToDollars } from './utils'
import { AccountType } from '@Types'
import isNumber from 'lodash/isNumber'

export interface InfoBarProps {
  appTitle: string
  accountTitle?: string
  accountType?: AccountType | null
  accountNumber?: string
  marginAmount?: number
  amountAvailable?: number
  buyingPower?: number
  scroll: boolean
  totalWidth: string
  metricWidth: string
  amountIsLoading: boolean
  marginStatus: 'idle' | 'error' | 'loading' | 'success'
}

function AccountInfoBar({
  appTitle,
  accountTitle,
  accountType,
  accountNumber,
  marginAmount,
  amountAvailable,
  buyingPower,
  scroll,
  metricWidth,
  totalWidth,
  amountIsLoading,
  marginStatus
}: InfoBarProps): ReactElement {
  const formatSummaryValues = (value: any) => (isNumber(value) ? numberToDollars(value) : 'Unavailable')

  const isMarginReady = marginStatus === 'success' || marginStatus === 'error'
  const equityValue = isMarginReady ? formatSummaryValues(marginAmount) : '-'
  const amountValue = !amountIsLoading ? formatSummaryValues(amountAvailable) : '-'
  const buyingPowerValue = isMarginReady ? formatSummaryValues(buyingPower) : '-'

  return (
    <VStack
      data-testid='summary-header'
      top={0}
      padding={0}
      width={totalWidth}
      className={`${styles.defaultContainer} ${scroll ? styles.stickyContainer : ''}`}
    >
      <Box data-testid='alert-center-container' w='100%'>
        <AlertCenterContainer />
      </Box>
      <HStack width='100%' padding={4} justifyContent='space-between'>
        <VStack alignItems='start'>
          <HStack spacing={2} pb={2}>
            <Text as='h5' textStyle='h1' className={styles.accountInfo}>
              {appTitle}
              <Link
                href={`${window.location.origin}/accounts/#/search/${accountNumber}`}
                color='blue.400'
                className={styles.accountLink}
              >
                {accountNumber}
              </Link>{' '}
              {accountType && `(${accountType}) ${accountTitle}`}
              {!accountType && <>loading...</>}
            </Text>
          </HStack>
          <Stack direction='row' spacing={8}>
            <MetricSummary
              className={styles.metricSummary}
              title='Account Equity'
              value={equityValue}
              width={metricWidth}
            />
            <MetricSummary
              className={styles.metricSummary}
              data-testid='summary-amount-available'
              title='Amount Available'
              value={amountValue}
              width={metricWidth}
              popoverHeader='Amount Available'
              popoverBody='This includes adjustments to the start of day balance for recent deposits, intraday activities, and pending debit interest and dividends.'
            />
            <MetricSummary
              className={styles.metricSummary}
              title='Buying Power'
              value={buyingPowerValue}
              width={metricWidth}
            />
          </Stack>
        </VStack>
      </HStack>
    </VStack>
  )
}

export default AccountInfoBar
